import React from 'preact/compat';
import {
    useTypeParam,
    useBuildToolParam,
    useCloudsParam,
    useNameParam,
    usePackageParam,
    useSampleCodeParam,
    useTestParam,
    useLanguageParam,
    useJavaVersionParam,
    useMicronautVersionParam
} from '../../state/parameterStore';
import { useAdvancedMode } from '../../state/advancedModeStore';
import RadioSelector from './RadioSelector';
import DropDownSelector from './DropDownSelector';
import CheckboxSelector from './CheckboxSelector';
import TextInput from './TextInput';
import Loading from '../Loading';

import Stack from '@mui/material/Stack';
import LauncherGenerate from './LauncherGenerate';

const LauncherForm = ({appName}) => {
    const [applicationType, setApplicationType, applicationTypeOptions, applicationTypeLoading] = useTypeParam();
    const [buildTool, setBuildTool, buildToolOptions, buildToolLoading] = useBuildToolParam();
    const [test, setTest, testOptions, testLoading] = useTestParam();
    const [cloud, setCloud, cloudOptions, cloudLoading] = useCloudsParam();
    const [language, setLanguage, languageOptions, languageLoading] = useLanguageParam()
    const [javaVersion, setJavaVersion, javaVersionOptions, javaVersionLoading] = useJavaVersionParam();
    const [micronautVersion, setMicronautVersion, micronautVersionOptions, micronautVersionLoading] = useMicronautVersionParam();
    const [applicationName, setApplicationName] = useNameParam();
    const [applicationPackage, setPackage] = usePackageParam();
    const [sampleCode, setSampleCode] = useSampleCodeParam();

    const [advancedMode, ] = useAdvancedMode();

    if (applicationTypeLoading || buildToolLoading || cloudLoading || testLoading
        || languageLoading || javaVersionLoading || micronautVersionLoading) {
        return <Loading />;
    }

    return (
        <>
        <Stack
            direction="row"
            sx={{
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '24px',
                marginTop: '21px',
            }}
        >
            <DropDownSelector label="Project Type" id="application-type"
                description="Project Type. Choose 'Application' for server-based projects and other for serverless"
                options={applicationTypeOptions}
                value={applicationType}
                setValue={setApplicationType}
            />
            <TextInput label="Project Name" id="project-name"
                description="Project Name. Must not include spaces or dots"
                value={applicationName} setValue={setApplicationName} placeholder="Example App"
            />
            <TextInput label="Base package" id="package"
                description="The package for all the classes of your project"
                value={applicationPackage} setValue={setPackage} placeholder="com.example"
            />
            {!advancedMode && <LauncherGenerate appName={appName}/>}
        </Stack>

        { !advancedMode &&
        <Stack
            direction="row"
            sx={{
                columnGap: '60px',
                rowGap: '40px',
                flexWrap: 'wrap',
                marginTop: '56px',
            }}
        >
            <CheckboxSelector label="Clouds" id="clouds" row
                description="Clouds that you want your project to support"
                options={cloudOptions}
                value={cloud}
                setValue={setCloud} />
            <RadioSelector label="Build Tool" id="build-tool" row
                options={[
                    {label: 'Gradle', value: buildTool === 'GRADLE_KOTLIN' ? 'GRADLE_KOTLIN' : 'GRADLE'},
                    {label: 'Maven', value: 'MAVEN'}
                ]}
                value={buildTool}
                setValue={setBuildTool} />
            <RadioSelector label="Java Version" id="java-version" row
                options={javaVersionOptions}
                value={javaVersion}
                setValue={setJavaVersion} />
            <RadioSelector label="Sample Code" id="sample-code" row
                description="Whether to create files with examples of using cloud services in the project template"
                options={[{label: "Yes", value: true}, {label: "No", value: false}]}
                value={sampleCode}
                setValue={(v) => setSampleCode(v !== "false")} />
        </Stack>
        }

        { advancedMode &&
        <Stack
            direction="row"
            sx={{
                gap: '40px',
                flexWrap: 'wrap',
                marginTop: '56px',
                marginBottom: '56px',
            }}
        >
            <CheckboxSelector label="Clouds" id="clouds"
                description="Clouds that you want your project to support"
                options={cloudOptions}
                value={cloud}
                setValue={setCloud} />
            <RadioSelector label="Build Tool" id="build-tool"
                description="Choose a tool for building your project. Refer to Gradle to choose your configuration language"
                options={buildToolOptions}
                value={buildTool}
                setValue={setBuildTool} />
            <RadioSelector label="Language" id="language"
                description="Choose a programming language that you are most comfortable using"
                options={languageOptions}
                value={language}
                setValue={setLanguage} />
            <RadioSelector label="Test Framework" id="test-framework"
                description="Framework for writing tests. Maven is the most commonly used Java test framework"
                options={testOptions}
                value={test}
                setValue={setTest} />
            <RadioSelector label="Java Version" id="java-version"
                options={javaVersionOptions}
                value={javaVersion}
                setValue={setJavaVersion} />
            <RadioSelector label="Micronaut Version" id="micronaut-version"
                options={micronautVersionOptions}
                value={micronautVersion}
                setValue={setMicronautVersion} />
            <RadioSelector label="Sample Code" id="sample-code"
                description="Whether to create files with examples of using cloud services in the project template"
                options={[{label: "Yes", value: true}, {label: "No", value: false}]}
                value={sampleCode}
                setValue={(v) => setSampleCode(v !== "false")} />
        </Stack>
        }
        {advancedMode && <LauncherGenerate appName={appName}/>}
        </>
    );
}

export default LauncherForm;
